import { useState, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  GenerateWithAIDialogProps,
  GenerateFormData,
} from "./GenerateWithAIDialog.types";
import {
  useGetAutomationListQuery,
  useGetCampaignListQuery,
  usePutAutomationGenerateMutation,
  usePutCampaignGenerateMutation,
} from "../../app/useGeneratedApi";

export function useGenerateWithAIDialog(props: GenerateWithAIDialogProps) {
  const { open, onTemplateGenerated } = props;

  const methods = useForm<GenerateFormData>({
    defaultValues: {
      templateType: "automation",
      workflowId: "",
    },
  });

  const { watch, reset, setValue } = methods;
  const templateType = watch("templateType");
  const workflowId = watch("workflowId");

  // State for template generation
  const [generatedHtml, setGeneratedHtml] = useState<string>("");
  const [generatedCss, setGeneratedCss] = useState<string>("");
  const [rawHtml, setRawHtml] = useState<string>("");
  const [currentOrder, setCurrentOrder] = useState<number>(0);
  const [nextOrder, setNextOrder] = useState<number>(0);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);

  const { data: automationsData, isLoading: isLoadingAutomations } =
    useGetAutomationListQuery({
      enabled: open && templateType === "automation",
    });

  const { data: campaignsData, isLoading: isLoadingCampaigns } =
    useGetCampaignListQuery({
      enabled: open && templateType === "campaign",
    });

  const automations =
    automationsData?.automations?.filter(
      (automation) => !automation.comingSoon
    ) || [];
  const campaigns = campaignsData?.campaigns || [];
  const isLoadingWorkflows = isLoadingAutomations || isLoadingCampaigns;

  const automationGenerateMutation = usePutAutomationGenerateMutation({
    onSuccess: (data) => {
      setGeneratedHtml(data.html);
      setGeneratedCss(data.css);
      setRawHtml(data.rawHtml || "");
      setCurrentOrder(data.currentOrder);
      setNextOrder(data.nextOrder);
      setHasGenerated(true);
      if (onTemplateGenerated) {
        onTemplateGenerated(data.html, data.css);
      }
      setIsGenerating(false);
    },
    onError: (error: any) => {
      setIsGenerating(false);
      setGenerateError(
        error.response?.data?.message ||
          "Failed to generate template. Please try again."
      );
    },
  });

  const campaignGenerateMutation = usePutCampaignGenerateMutation({
    onSuccess: (data) => {
      setGeneratedHtml(data.html);
      setGeneratedCss(data.css);
      setCurrentOrder(data.currentOrder);
      setNextOrder(data.nextOrder);
      setHasGenerated(true);
      if (onTemplateGenerated) {
        onTemplateGenerated(data.html, data.css);
      }
      setIsGenerating(false);
    },
    onError: (error: any) => {
      setIsGenerating(false);
      setGenerateError(
        error.response?.data?.message ||
          "Failed to generate template. Please try again."
      );
    },
  });

  const handleGenerate = useCallback(async () => {
    if (!workflowId) {
      setGenerateError("Please select a workflow");
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    if (templateType === "automation") {
      automationGenerateMutation.mutate({ data: { workflowId } });
    } else {
      campaignGenerateMutation.mutate({ data: { workflowId } });
    }
  }, [workflowId, onTemplateGenerated]);

  // Regenerate template - MOCK VERSION
  const handleRegenerate = useCallback(async () => {
    if (!workflowId) {
      setGenerateError("Please select a workflow");
      return;
    }

    setIsGenerating(true);
    setGenerateError(null);

    if (templateType === "automation") {
      automationGenerateMutation.mutate({ data: { workflowId, nextOrder } });
    } else {
      campaignGenerateMutation.mutate({ data: { workflowId, nextOrder } });
    }
  }, [workflowId, nextOrder, onTemplateGenerated]);

  useEffect(() => {
    if (open) {
      reset();
      setGeneratedHtml("");
      setGeneratedCss("");
      setRawHtml("");
      setCurrentOrder(0);
      setNextOrder(0);
      setHasGenerated(false);
      setGenerateError(null);
      setIsGenerating(false);
    }
  }, [open, reset]);

  useEffect(() => {
    setValue("workflowId", "");
    setGeneratedHtml("");
    setGeneratedCss("");
    setRawHtml("");
    setHasGenerated(false);
    setGenerateError(null);
    setIsGenerating(false);
  }, [templateType, setValue]);

  useEffect(() => {
    if (workflowId) {
      setGeneratedHtml("");
      setGeneratedCss("");
      setRawHtml("");
      setHasGenerated(false);
      setGenerateError(null);
    }
  }, [workflowId]);

  const workflowOptions =
    templateType === "automation" ? automations : campaigns;

  return {
    methods,
    templateType,
    workflowId,
    workflowOptions,
    isLoadingWorkflows,
    generatedHtml,
    generatedCss,
    rawHtml,
    isGenerating,
    generateError,
    hasGenerated,
    handleGenerate,
    handleRegenerate,
  };
}
